import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, GiftIcon, StoreIcon } from 'lucide-react';
import { shippingOptions, store } from '../../data/store';
import { formatPrice } from '../../utils/currency';
import { easeOut } from '../../utils/motion';
import { getShippingCost } from '../../utils/shipping';
import type { CheckoutState } from '../../hooks/useCheckout';
import { btnPrimary } from '../ui/buttonStyles';
import { FormField } from './FormField';
const reveal = {
  initial: {
    height: 0,
    opacity: 0
  },
  animate: {
    height: 'auto',
    opacity: 1
  },
  exit: {
    height: 0,
    opacity: 0
  },
  transition: {
    duration: 0.25,
    ease: easeOut
  }
};
export function DeliveryStep({
  checkout
}: {
  checkout: CheckoutState;
}) {
  const {
    form,
    errors,
    update,
    submitStep,
    subtotal
  } = checkout;
  return <form noValidate onSubmit={e => {
    e.preventDefault();
    submitStep('delivery');
  }} data-id="element-98" data-mp="3505pkgnofsnb">
      <fieldset data-id="element-99" data-mp="3a0jmp3nqpi6k">
        <legend className="sr-only" data-id="element-100" data-mp="yicqdio92iqc">Mode de livraison</legend>
        <div className="grid gap-3" data-id="element-101" data-mp="ygjftsained2">
          {shippingOptions.map(o => {
          const cost = getShippingCost(o, subtotal);
          const selected = form.shippingId === o.id;
          return <label key={o.id} className={`flex cursor-pointer items-start gap-4 rounded-md border p-4 transition-[border-color,background-color] duration-150 ${selected ? 'border-ink bg-card' : 'border-line hover:border-ink/40'}`} data-id="element-102" data-mp="18efyd55s9bl7">
                <input type="radio" name="shipping" value={o.id} checked={selected} onChange={() => update('shippingId', o.id)} className="mt-1 h-4 w-4 accent-clay" data-id="element-103" data-mp="phoa0hbdw6aa" />
                <span className="min-w-0 flex-1" data-id="element-104" data-mp="3t4eumhdgtw7z">
                  <span className="flex items-baseline justify-between gap-3" data-id="element-105" data-mp="3o6mzzflfrw03">
                    <span className="text-sm font-medium" data-id="element-106" data-mp="1s5bl1vvmeu5z">{o.label}</span>
                    <span className={`shrink-0 text-sm ${cost === 0 ? 'font-medium text-sage' : ''}`} data-id="element-107" data-mp="1x3msy12cuulk">
                      {cost === 0 ? 'Offert' : formatPrice(cost)}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-xs text-muted" data-id="element-108" data-mp="25wgc49kxae4p">
                    {o.delay} · {o.description}
                  </span>
                </span>
              </label>;
        })}
        </div>
      </fieldset>

      <AnimatePresence initial={false} mode="wait" data-id="element-109" data-mp="1e5ahu9rvvanc">
        {form.shippingId === 'pickup' ? <motion.div key="pickup" {...reveal} className="overflow-hidden" data-id="element-110" data-mp="1i4suki9jwff3">
            <div className="mt-4 flex gap-3 rounded-md bg-shell p-4 text-sm" data-id="element-111" data-mp="1n6i4tvoy8p24">
              <StoreIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-112" data-mp="2tm0wmwwq5jg3" />
              <div data-id="element-113" data-mp="2tnvm8oixxzqi">
                <p className="font-medium" data-id="element-114" data-mp="2yk2ref3ij77e">
                  {store.address}, {store.city}
                </p>
                <p className="mt-0.5 text-muted" data-id="element-115" data-mp="38osn438bpf36">
                  {store.hours[0].days}, {store.hours[0].time}. Nous vous écrivons dès que votre commande est prête — elle
                  vous sera remise par le créateur de permanence.
                </p>
              </div>
            </div>
          </motion.div> : <motion.div key="address" {...reveal} className="overflow-hidden" data-id="element-116" data-mp="2uwmfj5j8kgfc">
            <div className="mt-5 grid gap-4 sm:grid-cols-6" data-id="element-117" data-mp="2pz2qdal02j8m">
              <FormField id="address" label={form.shippingId === 'relay' ? 'Adresse (pour trouver le relais le plus proche)' : 'Adresse'} autoComplete="street-address" className="sm:col-span-6" value={form.address} onChange={e => update('address', e.target.value)} error={errors.address} data-id="element-118" data-mp="2kyycztfe24mm" />
              <FormField id="postalCode" label="Code postal" inputMode="numeric" autoComplete="postal-code" className="sm:col-span-2" maxLength={5} value={form.postalCode} onChange={e => update('postalCode', e.target.value.replace(/\D/g, ''))} error={errors.postalCode} data-id="element-119" data-mp="a6dntq7qowfh" />
              <FormField id="city" label="Ville" autoComplete="address-level2" className="sm:col-span-4" value={form.city} onChange={e => update('city', e.target.value)} error={errors.city} data-id="element-120" data-mp="c9flgcqyjvrt" />
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="mt-6 rounded-md border border-line p-4" data-id="element-121" data-mp="3pqonslvajhsb">
        <label className="flex cursor-pointer items-start gap-3" data-id="element-122" data-mp="3bqdt17qv1j1v">
          <input type="checkbox" checked={form.giftWrap} onChange={e => update('giftWrap', e.target.checked)} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-123" data-mp="3bs8gaspltn94" />
          <span className="text-sm" data-id="element-124" data-mp="2x59qf26eb0wi">
            <span className="flex items-center gap-2 font-medium" data-id="element-125" data-mp="2s55d1l0samai">
              <GiftIcon className="h-4 w-4" aria-hidden data-id="element-126" data-mp="36450ipck8v53" /> Emballage cadeau offert
            </span>
            <span className="mt-0.5 block text-muted" data-id="element-127" data-mp="322jg8ixp5ekv">Papier kraft, ficelle de lin et un mot écrit à la main.</span>
          </span>
        </label>
        <AnimatePresence initial={false} data-id="element-128" data-mp="3g0zvwzdjeff5">
          {form.giftWrap && <motion.div {...reveal} className="overflow-hidden" data-id="element-129" data-mp="3b3zq8pklneok">
              <label htmlFor="giftMessage" className="mb-1.5 mt-4 block text-sm" data-id="element-130" data-mp="3b1dgqke5ep3p">
                Votre message <span className="text-xs text-muted" data-id="element-131" data-mp="3ptx70kg7923q">(facultatif)</span>
              </label>
              <textarea id="giftMessage" rows={3} maxLength={200} value={form.giftMessage} onChange={e => update('giftMessage', e.target.value)} className="w-full resize-none rounded-md border border-line bg-card px-4 py-3 text-sm outline-none transition-[border-color] duration-150 focus:border-ink" placeholder="Joyeux anniversaire…" data-id="element-132" data-mp="3kvdkahts7mab" />
              <p className="mt-1 text-right text-xs text-muted" data-id="element-133" data-mp="30g6dccjdisvm">{form.giftMessage.length}/200</p>
            </motion.div>}
        </AnimatePresence>
      </div>

      <button type="submit" className={`${btnPrimary} mt-6 w-full sm:w-auto`} data-id="element-134" data-mp="3a7ar58xbc7ov">
        Continuer vers le paiement <ArrowRightIcon className="h-4 w-4" data-id="element-135" data-mp="3f52s0sfxr457" />
      </button>
    </form>;
}